/**
 * UserMenu — topbar avatar with the default actions documented in
 * `docs/frontend/035-frontend-app-shell-plan.md §3.6`. "Switch
 * organization" is intentionally absent (V1 is single-org per profile).
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMeContext } from '../../context/MeContextProvider';
import { CaretDownIcon, ProfileIcon, SettingsIcon, SignOutIcon } from './icons';

function getInitials(name: string | null | undefined): string {
  if (!name) return '؟';
  const parts = name
    .split(/\s+/)
    .filter((p) => p.length > 0)
    .slice(0, 2);
  if (parts.length === 0) return name.charAt(0);
  return parts.map((p) => p.charAt(0)).join('');
}

export function UserMenu() {
  const { me, signOut } = useMeContext();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!ref.current) return;
      if (e.target instanceof Node && !ref.current.contains(e.target)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!me) return null;
  const name = me.profile?.full_name ?? me.profile?.email ?? 'مستخدم';
  const orgName = me.organization?.name ?? (me.system_admin ? 'دعم النظام' : '');
  const role = me.system_admin
    ? 'System admin'
    : (me.roles.find((r) => r.category === 'agency' || r.category === 'client')?.key ?? '');

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className="user-menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="av av-sm">{getInitials(name)}</span>
        <span className="info">
          <span className="nm">{name}</span>
          <span className="rl">{role ? `${role}${orgName ? ` · ${orgName}` : ''}` : orgName}</span>
        </span>
        <CaretDownIcon className="" />
      </button>
      {open ? (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: '100%',
            insetInlineEnd: 0,
            marginTop: 8,
            width: 240,
            background: 'var(--paper)',
            border: '1px solid var(--line-2)',
            borderRadius: 'var(--r-md)',
            boxShadow: 'var(--sh-pop)',
            padding: 6,
            zIndex: 'var(--z-drawer)',
          }}
        >
          <div style={{ padding: 12, borderBottom: '1px solid var(--line)', marginBottom: 4 }}>
            <div style={{ fontSize: 13, fontWeight: 600 }}>{name}</div>
            <div className="tiny">{me.profile?.email ?? ''}</div>
            {role ? (
              <div style={{ marginTop: 6 }}>
                <span className="pill pill-info" style={{ fontSize: 10 }}>
                  {role}
                </span>
              </div>
            ) : null}
          </div>
          <Link
            to="/app/settings"
            role="menuitem"
            onClick={() => setOpen(false)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 10px',
              borderRadius: 'var(--r-sm)',
              fontSize: 13,
              color: 'var(--ink-2)',
            }}
          >
            <ProfileIcon />
            ملفي الشخصي
          </Link>
          <Link
            to="/app/settings"
            role="menuitem"
            onClick={() => setOpen(false)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 10px',
              borderRadius: 'var(--r-sm)',
              fontSize: 13,
              color: 'var(--ink-2)',
            }}
          >
            <SettingsIcon />
            تفضيلات الحساب
          </Link>
          <div style={{ height: 1, background: 'var(--line)', margin: '4px 0' }} />
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void signOut();
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 10px',
              borderRadius: 'var(--r-sm)',
              fontSize: 13,
              color: 'var(--rose-ink)',
              width: '100%',
              textAlign: 'start',
            }}
          >
            <SignOutIcon />
            تسجيل الخروج
          </button>
        </div>
      ) : null}
    </div>
  );
}
